/* DraftDrawer — iter97.91
 *
 * Global header pill that surfaces a doctor's in-progress drafts so they
 * can resume a half-filled case from anywhere in the app. Triggered by
 * the "Case ID is generated on New case click" change (iter97.90) which
 * now creates real persistent drafts the moment a doctor starts the
 * wizard — without this pill, those drafts would only be discoverable
 * via /doctor/cases.
 *
 * Behaviour:
 *   • Polls /doctor/draft-cases every 60s
 *   • Hidden entirely when there are no drafts
 *   • Click opens a dropdown listing the doctor's most-recent drafts
 *     (patient name + case code + relative time) — clicking a row
 *     navigates to the wizard at that draft.
 *
 * Stubs without a patient picked are excluded server-side, so a doctor
 * who repeatedly bounces off /doctor/cases/new doesn't see those
 * abandoned shells in the list.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PencilSimple, CaretDown, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

function relTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    } catch { return ""; }
}

export default function DraftDrawer() {
    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const load = async () => {
        try {
            const r = await api.get("/doctor/draft-cases");
            setDrafts(Array.isArray(r.data) ? r.data : []);
        } catch { /* non-fatal */ }
    };

    useEffect(() => {
        load();
        // Poll on a slow cadence — drafts are doctor-authored so they change
        // mainly when the doctor themselves edits one. 60s keeps the pill
        // honest without flooding the API.
        const t = setInterval(load, 60_000);
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(t);
            window.removeEventListener("focus", onFocus);
        };
    }, []);

    useEffect(() => {
        // Close dropdown on outside click.
        if (!open) return undefined;
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    if (!drafts.length) return null;

    const count = drafts.length;
    const label = count === 1 ? "1 draft" : `${count} drafts`;

    return (
        <div ref={ref} className="relative" data-testid="draft-drawer">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                    open
                        ? "bg-primary/10 border-primary/40 text-primary"
                        : "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100"
                }`}
                data-testid="draft-drawer-toggle"
                title="Resume a draft case"
            >
                <PencilSimple size={11} weight="fill" />
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{count}</span>
                <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] bg-card border border-border rounded-2xl shadow-xl overflow-hidden z-50"
                    data-testid="draft-drawer-list"
                >
                    <div className="px-4 py-3 border-b border-border bg-secondary/30">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Drafts in progress</div>
                        <div className="text-xs text-muted-foreground mt-0.5">Pick up where you left off — these cases were started but not yet submitted.</div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {drafts.map((d) => (
                            <button
                                key={d.id}
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate(`/doctor/cases/new?draft=${d.id}`);
                                }}
                                className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-secondary/40 transition-colors border-b border-border/60 last:border-b-0"
                                data-testid={`draft-drawer-item-${d.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate">{d.patient_name}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                                        <span className="font-mono tabular-nums">{d.code || "—"}</span>
                                        <span className="opacity-60">·</span>
                                        <span>{relTime(d.created_at)}</span>
                                        {d.planning_mode === "doctor" && (
                                            <>
                                                <span className="opacity-60">·</span>
                                                <span className="font-semibold text-violet-700">Self-planned</span>
                                            </>
                                        )}
                                        {d.case_kind === "refinement" && (
                                            <>
                                                <span className="opacity-60">·</span>
                                                <span className="font-semibold text-amber-700">Refinement</span>
                                            </>
                                        )}
                                        {d.case_kind === "retainer" && (
                                            <>
                                                <span className="opacity-60">·</span>
                                                <span className="font-semibold text-blue-700">Retainer</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <ArrowRight size={14} weight="bold" className="text-muted-foreground shrink-0" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
